import type { Certificate, CertificateState } from '../wire';

/**
 * Certificate state, derived on this device — the same arithmetic
 * `packages/store`'s `certificateState` does, for the same reason
 * `routes/certificates.ts` gives:
 *
 * > A certificate is expired when `expires_on < today` and nothing has redeemed
 * > it. Every read computes that, so a sweeper that has not run cannot cause a
 * > dead certificate to be honoured.
 *
 * On the device the argument is stronger, not weaker. `state` arrives on the
 * mirrored row computed against the server's `today` — which may be the `today`
 * of whenever this iPad last had signal. Rendering that stored word would tell
 * the counter that a certificate which expired on Tuesday is still open. So the
 * word is recomputed against the tenant's *current* day every time it is shown,
 * and the mirrored `state` is never read.
 *
 * This is not the device deciding anything: the checkout still names a code and
 * `POST /v1/settlements` still refuses an expired one with its own `today`. It
 * is the device declining to repeat a fact it knows has an expiry date.
 */
export function certificateState(certificate: Certificate, today: string): CertificateState {
  // Voided is checked first and it is a different fact from redeemed (0046):
  // a redemption means the guest got her value, a void means she got her money.
  // A till that said "spent" about a refunded card would be telling somebody
  // holding it something untrue.
  if (certificate.voided_at !== null) return 'voided';
  if (certificate.redeemed_at !== null || certificate.remaining_cents <= 0) return 'redeemed';
  if (certificate.expires_on !== null && certificate.expires_on < today) return 'expired';
  return 'open';
}

/**
 * What the shop owes in treatments, and what that set is worth at face.
 *
 * Both numbers, never one, for the reason `certificateLiabilityRoute` states:
 * `outstanding_cents` is what is actually left on open certificates, while
 * `face_value_cents` is the same set at face value and is what the §9 cutover
 * gate compares. They differ exactly when a partial redemption is in play, and
 * quietly showing whichever is smaller would be a reconciliation nobody could
 * explain.
 */
export interface CertificateLiability {
  outstandingCents: number;
  faceValueCents: number;
  expiredCents: number;
  redeemedCents: number;
  /** Unredeemed value on refunded certificates (0046). Not a liability. */
  voidedCents: number;
  open: Certificate[];
}

export function certificateLiability(
  certificates: readonly Certificate[],
  today: string,
): CertificateLiability {
  const liability: CertificateLiability = {
    outstandingCents: 0,
    faceValueCents: 0,
    expiredCents: 0,
    redeemedCents: 0,
    voidedCents: 0,
    open: [],
  };

  for (const certificate of certificates) {
    switch (certificateState(certificate, today)) {
      case 'open':
        liability.outstandingCents += certificate.remaining_cents;
        liability.faceValueCents += certificate.value_cents;
        liability.open.push(certificate);
        break;
      case 'expired':
        liability.expiredCents += certificate.remaining_cents;
        break;
      case 'redeemed':
        liability.redeemedCents += certificate.applied_cents;
        break;
      // A refunded certificate is money already given back: not outstanding,
      // not expired, and emphatically not redeemed.
      case 'voided':
        liability.voidedCents += certificate.remaining_cents;
        break;
    }
  }
  return liability;
}

/**
 * Find the code the owner typed.
 *
 * Case-insensitive, because `findCertificateByCode` in `packages/store` compares
 * `upper(code) = upper($1)` and a till that was stricter than the server would
 * refuse a card the shop would honour. Dashes are *not* normalized away: they
 * are part of the code the server stored, and inventing a normalization the
 * server does not share is how the two stop agreeing about what a card is.
 */
export function findCertificateByCode(
  certificates: readonly Certificate[],
  code: string,
): Certificate | undefined {
  const needle = code.trim().toUpperCase();
  if (needle === '') return undefined;
  return certificates.find((c) => c.code.toUpperCase() === needle);
}
